type ISearchPart = [string, string, boolean] // [variable, value, isExact]

const getSearchParts = (searchString: string) => {
  const searchParts: ISearchPart[] = []
  let searchVariable = ""
  let searchValue = ""
  let isExact = false
  let inQuotes = false
  let quoteChar = ""
  let currentToken = ""

  const addSearchPart = () => {
    // Add search part even if searchValue is empty (for exact match of empty string)
    if (searchValue || isExact) {
      searchParts.push([searchVariable, searchValue.toLocaleLowerCase(), isExact])
      // Reset for next search part
      searchVariable = ""
      searchValue = ""
      isExact = false
    }
  }

  // Parse character by character to handle quotes properly
  for (let i = 0; i < searchString.length; i++) {
    const char = searchString[i]

    if ((char === '"' || char === "'") && !inQuotes) {
      // Start of quoted section
      inQuotes = true
      quoteChar = char
      isExact = true
      continue
    }

    if (char === quoteChar && inQuotes) {
      // End of quoted section
      inQuotes = false
      quoteChar = ""
      continue
    }

    if (char === " " && !inQuotes) {
      // Space outside quotes - might be end of a search part
      if (currentToken) {
        if (currentToken.includes(":") && !searchValue) {
          // This is a key:value start
          const parts = currentToken.split(":", 2)
          searchVariable = (parts[0] ?? "").toLocaleLowerCase()
          searchValue = parts[1] ?? ""
        } else if (currentToken.includes(":") && searchValue) {
          // We have a current search part and starting a new field:value
          // Complete the current search part first
          addSearchPart()
          const parts = currentToken.split(":", 2)
          searchVariable = (parts[0] ?? "").toLocaleLowerCase()
          searchValue = parts[1] ?? ""
        } else if (searchVariable || searchValue) {
          // Add to current search value
          if (searchValue) {
            searchValue += " "
          }
          searchValue += currentToken
        } else {
          // This is a standalone token, add as search part
          searchValue = currentToken
          addSearchPart()
        }
        currentToken = ""
      } else if (searchValue && !searchVariable && !inQuotes) {
        // We have a completed search part
        addSearchPart()
      }
      continue
    }

    if (char === ":" && !inQuotes) {
      // Colon for key:value syntax
      if (searchValue) {
        // We have a current search part, complete it first
        addSearchPart()
        searchVariable = currentToken.toLocaleLowerCase()
        currentToken = ""
      } else {
        searchVariable = currentToken.toLocaleLowerCase()
        currentToken = ""
      }
      continue
    }

    currentToken += char
  }

  // Process remaining token
  if (currentToken) {
    if (currentToken.includes(":") && !searchValue) {
      const parts = currentToken.split(":", 2)
      searchVariable = (parts[0] ?? "").toLocaleLowerCase()
      searchValue = parts[1] ?? ""
    } else {
      if (searchValue) {
        searchValue += " "
      }
      searchValue += currentToken
    }
  }

  // Add final search part
  addSearchPart()

  return searchParts
}

type ISearchMatcher = [keyMatches: ((key: string) => boolean) | undefined, string, boolean]

// Keys repeat across rows, so each one is lowercased once per search rather than once per value.
const toMatchers = (searchParts: ISearchPart[]): ISearchMatcher[] =>
  searchParts.map(([searchVariable, searchValue, isExact]) => {
    if (!searchVariable) return [undefined, searchValue, isExact]
    const keyMatchCache = new Map<string, boolean>()
    const keyMatches = (key: string) => {
      let matches = keyMatchCache.get(key)
      if (matches === undefined) {
        matches = key.toLocaleLowerCase().includes(searchVariable)
        keyMatchCache.set(key, matches)
      }
      return matches
    }
    return [keyMatches, searchValue, isExact]
  })

const matchesSearch = (
  key: string,
  value: any,
  keyMatches: ((key: string) => boolean) | undefined,
  searchValue: string,
  isExact: boolean,
): boolean => {
  if (value === null || value === undefined) {
    return false
  }
  if (Array.isArray(value)) {
    for (const val of value) {
      if (matchesSearch(key, val, keyMatches, searchValue, isExact)) return true
    }
    return false
  }

  if (typeof value === "object") {
    for (const k of Object.keys(value)) {
      if (matchesSearch(k, value[k], keyMatches, searchValue, isExact)) return true
    }
    return false
  }

  if (keyMatches && !keyMatches(key)) {
    return false
  }

  const valueStr = value.toString().toLocaleLowerCase()

  if (isExact) {
    // Exact match - the value must equal the search value exactly
    return valueStr === searchValue
  } else {
    // Substring match - the value must contain the search value
    return valueStr.includes(searchValue)
  }
}

const matchesAllParts = (
  obj: Record<string, any>,
  matchers: ISearchMatcher[],
  keys?: readonly string[],
) =>
  matchers.every(([keyMatches, searchValue, isExact]) =>
    Object.keys(obj).some(
      (key) =>
        (!keys || keys.includes(key)) &&
        matchesSearch(key, obj[key], keyMatches, searchValue, isExact),
    ),
  )

export const filterOnSearchString = <T extends Record<string, any>>(
  objs: T[],
  searchString: string,
): T[] => {
  // Filter on search, so if users press "sko" it will look filter out objects that have a "sko" as a substring of one of the keys
  // users can also write "name:sko" and it will look in all keys containing "name" and values containing "sko"
  // notice that the nested objects are also considered
  // users can use quotes for exact matches: "1000" will only match "1000", not "100000"
  // users can combine field-specific and exact searches: color:"white" will match exact "white" in color fields
  const matchers = toMatchers(getSearchParts(searchString))
  return objs.filter((obj) => matchesAllParts(obj, matchers))
}

/**
 * {@link filterOnSearchString}, plus a rank per match: the index of the first tier whose fields
 * match every search part on their own, or `rankTiers.length` when only other fields do.
 * `toSearchable` picks the fields that are searched; hits still return the original item.
 */
export const rankOnSearchString = <
  T extends Record<string, any>,
  S extends Record<string, any> = T,
>(
  objs: readonly T[],
  searchString: string,
  rankTiers: readonly (readonly (keyof S & string)[])[],
  toSearchable: (item: T) => S = (item) => item as unknown as S,
): { item: T; rank: number }[] => {
  const matchers = toMatchers(getSearchParts(searchString))
  return objs.flatMap((item) => {
    const record = toSearchable(item)
    if (!matchesAllParts(record, matchers)) return []
    const tier = rankTiers.findIndex((keys) => matchesAllParts(record, matchers, keys))
    return [{ item, rank: tier === -1 ? rankTiers.length : tier }]
  })
}
